import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { z } from 'zod';

import { FIRST_INDEX } from '@/constants';
import { useAppDispatch, useAppSelector } from '@/store';
import {
  selectAuthError,
  selectAuthStatus,
  selectSetPasswordStage,
} from '@/store/selectors/auth';
import { registerPasswordThunk } from '@/store/thunks/auth/registerPasswordThunk';
import { setRegistrationStep } from '@/utils/sessionStorage';
import type { SetPasswordStage } from '@/types/AuthState';

const MIN_PASSWORD_LENGTH = 12;

const schema = z
  .object({
    password: z
      .string()
      .min(MIN_PASSWORD_LENGTH, 'Password must be at least 12 characters long')
      .regex(/[A-Z]/, 'Must contain at least 1 uppercase letter')
      .regex(/[a-z]/, 'Must contain at least 1 lowercase letter')
      .regex(/\d/, 'Must contain at least 1 number')
      .regex(/[^A-Za-z0-9]/, 'Must contain at least 1 special character'),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

interface UsePasswordFormState {
  password: string;
  confirmPassword: string;
  showPassword: boolean;
  showConfirmPassword: boolean;
  hasSubmitted: boolean;
  passwordError: string | undefined;
  isSubmitting: boolean;
  isContinueDisabled: boolean;
  globalError: string | undefined;
  stage: SetPasswordStage | undefined;
}

interface UsePasswordFormHandlers {
  handlePasswordChange: (value: string) => void;
  handleConfirmPasswordChange: (value: string) => void;
  handleTogglePassword: () => void;
  handleToggleConfirmPassword: () => void;
  handleSubmit: () => void;
}

interface UsePasswordFormReturn {
  state: UsePasswordFormState;
  handlers: UsePasswordFormHandlers;
}

export function usePasswordForm(): UsePasswordFormReturn {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const authStatus = useAppSelector(selectAuthStatus);
  const authError = useAppSelector(selectAuthError);
  const stage = useAppSelector(selectSetPasswordStage);
  const isSubmitting = authStatus === 'loading';

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const parsed = useMemo(
    () => schema.safeParse({ password, confirmPassword }),
    [password, confirmPassword],
  );

  const passwordError = useMemo(() => {
    if (!hasSubmitted || parsed.success) return undefined;
    return parsed.error.flatten().fieldErrors.password?.[FIRST_INDEX];
  }, [hasSubmitted, parsed]);

  const isContinueDisabled = isSubmitting || !parsed.success || stage === 'postCommit';

  const handlePasswordChange = useCallback((value: string): void => {
    setPassword(value);
  }, []);

  const handleConfirmPasswordChange = useCallback((value: string): void => {
    setConfirmPassword(value);
  }, []);

  const handleTogglePassword = useCallback((): void => {
    setShowPassword((prev) => !prev);
  }, []);

  const handleToggleConfirmPassword = useCallback((): void => {
    setShowConfirmPassword((prev) => !prev);
  }, []);

  const handleSubmit = useCallback(() => {
    setHasSubmitted(true);
    const currentParsed = schema.safeParse({ password, confirmPassword });
    if (!currentParsed.success) return;

    void (async() => {
      const action = await dispatch(
        registerPasswordThunk({ password: currentParsed.data.password })
      );
      if (registerPasswordThunk.fulfilled.match(action)) {
        setRegistrationStep('complete');
        await navigate({ to: '/registration/success' });
      }
    })();
  }, [confirmPassword, dispatch, navigate, password]);

  return {
    state: {
      password,
      confirmPassword,
      showPassword,
      showConfirmPassword,
      hasSubmitted,
      passwordError,
      isSubmitting,
      isContinueDisabled,
      globalError: authError,
      stage,
    },
    handlers: {
      handlePasswordChange,
      handleConfirmPasswordChange,
      handleTogglePassword,
      handleToggleConfirmPassword,
      handleSubmit,
    },
  };
}
